import React from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { 
  Container, 
  AppBar, 
  Toolbar, 
  Typography, 
  Box,
  Paper,
  IconButton,
  Button
} from '@mui/material';
import { Home, ArrowBack } from '@mui/icons-material';
import { Link } from 'react-router-dom';

const theme = createTheme({
  palette: {
    primary: { main: '#1976d2' },
    secondary: { main: '#dc004e' },
  },
});

function MUILayoutDocsPage() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      
      <AppBar position="static">
        <Toolbar>
          <IconButton 
            component={Link} 
            to="/" 
            color="inherit" 
            sx={{ mr: 2 }}
          >
            <Home />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            MUI Layout Documentation
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        
        <Paper sx={{ p: 4 }}>
          <Typography variant="h3" gutterBottom>
            MUI 레이아웃 시스템
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 3 }}>
            MUI의 레이아웃 시스템은 CSS Flexbox와 Grid를 기반으로 합니다. 
            반응형 웹 디자인을 쉽게 구현할 수 있도록 다양한 컴포넌트와 유틸리티를 제공합니다.
          </Typography>

          <Typography variant="h4" sx={{ mt: 4, mb: 2 }}>
            Grid System
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            MUI Grid는 12컬럼 시스템을 사용하며, 다양한 화면 크기에 따라 자동으로 조정됩니다.
          </Typography>
          
          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            기본 사용법
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
{`import { Grid } from '@mui/material';

<Grid container spacing={2}>
  <Grid item xs={12} sm={6} md={4}>
    <Paper>항목 1</Paper>
  </Grid>
  <Grid item xs={12} sm={6} md={4}>
    <Paper>항목 2</Paper>
  </Grid>
</Grid>`}
            </Typography>
          </Paper>

          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            Grid 속성
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            • container: 그리드 컨테이너 역할<br/>
            • item: 그리드 아이템 역할<br/>
            • spacing: 아이템 간 간격 (0-10)<br/>
            • xs, sm, md, lg, xl: 브레이크포인트별 너비 (1-12)
          </Typography>

          <Typography variant="h4" sx={{ mt: 4, mb: 2 }}>
            Flexbox 레이아웃
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Box 컴포넌트를 사용하여 Flexbox 레이아웃을 쉽게 구현할 수 있습니다. 중앙 정렬, 균등 배치 등이 간단합니다.
          </Typography>
          
          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            Box 컴포넌트로 Flexbox
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
{`<Box sx={{ 
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100vh'
}}>
  <Typography>중앙 정렬</Typography>
</Box>`}
            </Typography>
          </Paper>

          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            자주 사용하는 Flexbox 속성
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
{`sx={{
  display: 'flex',
  flexDirection: 'row',      // row, column
  justifyContent: 'center',  // flex-start, center, flex-end, space-between
  alignItems: 'center',      // flex-start, center, flex-end, stretch
  gap: 2,                    // 아이템 간 간격
}}`}
            </Typography>
          </Paper>

          <Typography variant="h4" sx={{ mt: 4, mb: 2 }}>
            Stack 컴포넌트
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Stack은 요소들을 일정한 간격으로 정렬하는 간단한 레이아웃 컴포넌트입니다. 버튼 그룹이나 폼 필드 정렬에 유용합니다.
          </Typography>
          
          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            수직 정렬
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
{`import { Stack } from '@mui/material';

<Stack spacing={2}>
  <Button>버튼 1</Button>
  <Button>버튼 2</Button>
  <Button>버튼 3</Button>
</Stack>`}
            </Typography>
          </Paper>

          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            수평 정렬
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
{`<Stack direction="row" spacing={2}>
  <Button>버튼 1</Button>
  <Button>버튼 2</Button>
</Stack>`}
            </Typography>
          </Paper>

          <Typography variant="h4" sx={{ mt: 4, mb: 2 }}>
            실제 레이아웃 예시
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            실무에서 자주 사용하는 대시보드 레이아웃 패턴입니다. 헤더, 사이드바, 메인 콘텐츠 영역으로 구성됩니다.
          </Typography>
          
          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            대시보드 레이아웃
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
{`<Container maxWidth="lg">
  <Grid container spacing={3}>
    {/* 헤더 */}
    <Grid item xs={12}>
      <Paper sx={{ p: 2 }}>
        <Typography variant="h4">대시보드</Typography>
      </Paper>
    </Grid>
    
    {/* 사이드바 */}
    <Grid item xs={12} md={3}>
      <Paper sx={{ p: 2, height: 400 }}>
        사이드바
      </Paper>
    </Grid>
    
    {/* 메인 콘텐츠 */}
    <Grid item xs={12} md={9}>
      <Paper sx={{ p: 2, height: 400 }}>
        메인 콘텐츠
      </Paper>
    </Grid>
  </Grid>
</Container>`}
            </Typography>
          </Paper>

          <Typography variant="h4" sx={{ mt: 4, mb: 2 }}>
            유용한 레이아웃 팁
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            자주 사용하는 레이아웃 패턴들입니다. 전체 높이 채우기와 중앙 정렬은 특히 많이 활용됩니다.
          </Typography>
          
          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            전체 높이 채우기
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
{`<Box sx={{ minHeight: '100vh' }}>
  {/* 내용 */}
</Box>`}
            </Typography>
          </Paper>

          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            중앙 정렬
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
{`<Box sx={{
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh'
}}>
  <Paper sx={{ p: 4 }}>
    중앙 정렬된 카드
  </Paper>
</Box>`}
            </Typography>
          </Paper>

        </Paper>

        <Box sx={{ mt: 3 }}>
          <Button 
            component={Link}
            to="/docs/mui-theme"
            variant="outlined"
            startIcon={<ArrowBack />}
          >
            Previous: Theme
          </Button>
        </Box>

      </Container>
    </ThemeProvider>
  );
}

export default MUILayoutDocsPage;
